import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { PermissionEnum } from "../model/permission-enum";
import { AuthenticationService } from "./authentication.service";

@Injectable({
  providedIn: "root",
})
export class DeepLinkService {
  private isNativePlatform: boolean = false;
  private readonly adminHomePath = "/employee-interface";

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
  ) {
    this.checkPlatform();
  }

  private async checkPlatform() {
    try {
      const { Capacitor } = await import("@capacitor/core");
      this.isNativePlatform = Capacitor.isNativePlatform();
    } catch {
      this.isNativePlatform = false;
    }
  }

  async initialize() {
    await this.checkPlatform();

    if (!this.isNativePlatform) {
      return;
    }

    try {
      const { App } = await import("@capacitor/app");

      App.addListener("appUrlOpen", (event: any) => {
        this.handleDeepLink(event.url);
      });

      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
        }
      });

      App.addListener("backButton", async ({ canGoBack }) => {
        await this.authenticationService.waitForInitialization();

        if (this.isAdminHomeRoute() && this.hasAdminPermission()) {
          App.exitApp();
          return;
        }

        if (!canGoBack) {
          App.exitApp();
        } else {
          window.history.back();
        }
      });
    } catch {}
  }

  private isAdminHomeRoute(): boolean {
    const cleanUrl = this.router.url.split("?")[0].split("#")[0];
    return cleanUrl === this.adminHomePath;
  }

  private hasAdminPermission(): boolean {
    return this.authenticationService.currentUserPermissions.some(
      (permission: PermissionEnum) => permission == PermissionEnum.Admin,
    );
  }

  private handleDeepLink(url: string) {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    const params = new URLSearchParams(urlObj.search);

    if (path.includes("profile")) {
      const userId = path.split("/").pop();
      if (userId) {
        this.router.navigate(["/profile", userId]);
      }
    } else if (path.includes("match")) {
      const matchId = path.split("/").pop();
      if (matchId) {
        this.router.navigate(["/your-match", matchId]);
      }
    } else if (path.includes("court")) {
      const courtId = path.split("/").pop();
      if (courtId) {
        this.router.navigate(["/book-a-court", courtId]);
      }
    } else if (path.includes("club")) {
      const clubId = path.split("/").pop();
      if (clubId) {
        this.router.navigate(["/my-club", clubId]);
      }
    } else if (path.includes("notification")) {
      this.router.navigate(["/notification"]);
    } else if (path.includes("ranking")) {
      this.router.navigate(["/rb-rank"]);
    } else if (path === "/" || path === "") {
      this.router.navigate(["/home"]);
    } else {
      this.router.navigateByUrl(path);
    }
  }

  async getAppInfo() {
    if (!this.isNativePlatform) {
      return null;
    }

    try {
      const { App } = await import("@capacitor/app");
      const info = await App.getInfo();
      return info;
    } catch (error) {
      return null;
    }
  }

  async getLaunchUrl() {
    if (!this.isNativePlatform) {
      return null;
    }

    try {
      const { App } = await import("@capacitor/app");
      const result = await App.getLaunchUrl();
      return result?.url || null;
    } catch (error) {
      return null;
    }
  }

  async removeListeners() {
    if (!this.isNativePlatform) {
      return;
    }

    try {
      const { App } = await import("@capacitor/app");
      App.removeAllListeners();
    } catch {}
  }
}
